import { createContext, useState, useCallback, useEffect, useContext, useMemo } from 'react';
import { createPermissionConfig } from './helpers/createPermissionsConfig';
import { buildInitialPermissionsConfig } from './helpers/buildInitialPermissionsConfig';
import { AccessClassifications, PermissionLevels, AssignmentTabTypes, PermissionableUserSets, DEFAULT_ALLOWED_PERMISSION_LEVELS } from './Constants';
import usageTracker from '../../utils/usageTracker';
import usePaginatedUsers from '../../hooks/usePaginatedUsers';
import { jsx as _jsx } from "react/jsx-runtime";
const {
  EVERYONE
} = AccessClassifications;
const {
  EDIT
} = PermissionLevels;
const {
  Teams
} = AssignmentTabTypes;
const {
  SINGLE_USER
} = PermissionableUserSets;
export const LoadUserOptionsPageSize = 50;
const defaultGranularPermissionsContext = {
  use: 'edit',
  teams: [],
  users: [],
  totalUsers: 0,
  loadUserOptions: undefined,
  emailCache: {},
  initialState: [],
  selectedTeamIds: new Set(),
  selectTeamId: () => {},
  deselectTeamId: () => {},
  selectedUserIds: new Set(),
  selectUserId: () => {},
  deselectUserId: () => {},
  permissionLevel: EDIT,
  setPermissionLevel: () => {},
  accessClassification: EVERYONE,
  setAccessClassification: () => {},
  assignmentTab: Teams,
  setAssignmentTab: () => {},
  updateAssignedEntities: () => {},
  assignedEntities: {},
  setAssignedEntities: () => {},
  createPermissionConfig: () => [],
  initializePermissionConfig: () => {},
  requestsLoading: false,
  setRequestsLoading: () => {},
  requestsError: false,
  setRequestsError: () => {},
  removeAssignedEntity: () => {},
  bulkAssignPermissionLevel: () => {},
  assetOwnerId: null,
  isTeamPermissionChanged: false,
  setIsTeamPermissionChanged: () => {},
  trackPanelAction: () => {},
  canUserBeGivenViewAccess: () => true,
  canUserBeGivenEditAccess: () => true,
  editAccessDisabledTooltip: null,
  viewAccessDisabledTooltip: null,
  allowedPermissionLevels: DEFAULT_ALLOWED_PERMISSION_LEVELS,
  accessStatePickerExtraComponentSlot: null,
  userIdsToDisplay: undefined,
  assetOwnerIds: []
};
export const GranularPermissionsContext = /*#__PURE__*/createContext(defaultGranularPermissionsContext);
export const useGranularPermissionsContext = () => {
  const context = useContext(GranularPermissionsContext);
  return context;
};
const useAssignedState = (setAssignedEntities, selectedUserIds, selectedTeamIds) => {
  const updateAssignedEntities = useCallback((id, permissionLevelValue, userType) => {
    setAssignedEntities(prevTeams => Object.assign({}, prevTeams, {
      [id]: {
        level: permissionLevelValue,
        type: userType
      }
    }));
  }, [setAssignedEntities]);
  const bulkAssignPermissionLevel = useCallback((permissionLevel, entityType, _selectedIds) => {
    const selectedIds = _selectedIds !== null && _selectedIds !== void 0 ? _selectedIds : entityType === SINGLE_USER ? selectedUserIds : selectedTeamIds;
    selectedIds.forEach(id => {
      updateAssignedEntities(id, permissionLevel, entityType);
    });
  }, [updateAssignedEntities, selectedUserIds, selectedTeamIds]);
  const removeAssignedEntity = useCallback(id => {
    setAssignedEntities(prevTeams => {
      const newTeams = Object.assign({}, prevTeams);
      delete newTeams[id];
      return newTeams;
    });
  }, [setAssignedEntities]);
  return {
    updateAssignedEntities,
    removeAssignedEntity,
    bulkAssignPermissionLevel
  };
};
const useSelectionState = initialSelection => {
  const [selected, setSelected] = useState(initialSelection);
  const select = useCallback(id => {
    setSelected(prev => new Set([...prev, id]));
  }, []);
  const deselect = useCallback(id => {
    setSelected(prev => {
      const newSet = new Set(prev);
      newSet.delete(id);
      return newSet;
    });
  }, []);
  return {
    select,
    deselect,
    selected
  };
};
export const GranularPermissionsProvider = props => {
  const {
    use,
    initialUserIds = new Set(),
    initialTeamIds = new Set(),
    initialAssignmentTab = Teams,
    initialRequestsLoading,
    initialRequestsError,
    initialTeams,
    initialStateValue,
    assetOwnerId = null,
    canUserBeGivenViewAccess,
    canUserBeGivenEditAccess,
    editAccessDisabledTooltip,
    viewAccessDisabledTooltip,
    allowedPermissionLevels = DEFAULT_ALLOWED_PERMISSION_LEVELS,
    accessStatePickerExtraComponentSlot = null,
    userIdsToDisplay,
    assetOwnerIds = assetOwnerId ? [assetOwnerId] : []
  } = props;
  const [initialUsers, setInitialUsers] = useState(undefined);
  const [totalUsers, setTotalUsers] = useState(0);
  const {
    loadOptions,
    bulkFetchUsers,
    emailCache
  } = usePaginatedUsers(LoadUserOptionsPageSize, userIdsToDisplay);
  if (loadOptions && !initialUsers) {
    loadOptions('', (error, data) => {
      if (error) {
        console.error('Error loading users:', error);
      } else {
        setInitialUsers(data === null || data === void 0 ? void 0 : data.options);
        if (data !== null && data !== void 0 && data.pagination.total) {
          setTotalUsers(data.pagination.total);
        }
      }
    }, {
      offset: 0
    });
  }
  const initial = useMemo(() => buildInitialPermissionsConfig(initialStateValue, initialTeams), [initialStateValue, initialTeams]);
  const [permissionLevel, setPermissionLevel] = useState(initial.permissionLevel);
  const [accessClassification, setAccessClassification] = useState(initial.accessClassification);
  const [assignedEntities, setAssignedEntities] = useState(initial.assignedEntities);
  if (Object.keys(assignedEntities).length > 0) {
    const userIds = Object.keys(assignedEntities).map(Number).filter(id => id);
    if (userIds.length > 0) {
      bulkFetchUsers(Object.keys(assignedEntities).map(Number), true);
    }
  }
  useEffect(() => {
    setAssignedEntities(initial.assignedEntities);
  }, [initial.assignedEntities, initialTeams]);
  const [assignmentTab, setAssignmentTab] = useState(initialAssignmentTab);
  const [isTeamPermissionChanged, setIsTeamPermissionChanged] = useState(false);
  const {
    selected: selectedUserIds,
    select: selectUserId,
    deselect: deselectUserId
  } = useSelectionState(initialUserIds);
  const {
    selected: selectedTeamIds,
    select: selectTeamId,
    deselect: deselectTeamId
  } = useSelectionState(initialTeamIds);
  const assignedState = useAssignedState(setAssignedEntities, selectedUserIds, selectedTeamIds);
  const settingsState = useMemo(() => Object.assign({
    use,
    assignmentTab,
    setAssignmentTab,
    accessClassification,
    setAccessClassification,
    assignedEntities,
    setAssignedEntities,
    permissionLevel,
    setPermissionLevel
  }, assignedState, {
    requestsLoading: initialRequestsLoading,
    teams: initialTeams,
    users: initialUsers || [],
    totalUsers,
    loadUserOptions: loadOptions,
    emailCache,
    initialState: initialStateValue,
    requestsError: initialRequestsError,
    setRequestsLoading: () => {},
    setRequestsError: () => {},
    selectedUserIds,
    selectUserId,
    deselectUserId,
    selectedTeamIds,
    selectTeamId,
    deselectTeamId,
    createPermissionConfig: () => createPermissionConfig(assignedEntities, accessClassification, permissionLevel),
    initializePermissionConfig: nextInitState => {
      const initialConfig = buildInitialPermissionsConfig(nextInitState, initialTeams);
      setAccessClassification(initialConfig.accessClassification);
      setPermissionLevel(initialConfig.permissionLevel);
      setAssignedEntities(initialConfig.assignedEntities);
    },
    trackPanelAction: (actionType, assetType) => {
      usageTracker.track('permissionsPanelClick', {
        actionType,
        assetType,
        accessClassification,
        permissionLevel,
        numOfAssignedEntities: Object.keys(assignedEntities).length
      });
    },
    assetOwnerId,
    setIsTeamPermissionChanged,
    isTeamPermissionChanged,
    canUserBeGivenViewAccess,
    canUserBeGivenEditAccess,
    editAccessDisabledTooltip,
    viewAccessDisabledTooltip,
    allowedPermissionLevels,
    accessStatePickerExtraComponentSlot,
    userIdsToDisplay,
    assetOwnerIds
  }), [use, assignmentTab, assignedState, initialRequestsLoading, initialTeams, initialUsers, totalUsers, loadOptions, emailCache, initialStateValue, initialRequestsError, selectedUserIds, selectUserId, deselectUserId, selectedTeamIds, selectTeamId, deselectTeamId, assetOwnerId, isTeamPermissionChanged, assignedEntities, accessClassification, permissionLevel, canUserBeGivenViewAccess, canUserBeGivenEditAccess, editAccessDisabledTooltip, viewAccessDisabledTooltip, allowedPermissionLevels, accessStatePickerExtraComponentSlot, userIdsToDisplay, assetOwnerIds]);
  return /*#__PURE__*/_jsx(GranularPermissionsContext.Provider, {
    value: Object.assign({}, settingsState),
    children: props.children
  });
};